import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdAddCircleOutline } from "react-icons/md";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import Select from "react-select";
import * as Yup from "yup";

import Button from "../../../components/Button";
import SuggestedAssessmentsModal from "../../../components/modals/teacher/SuggestedAssessmentsModal";
import { useTheme } from "../../../context/ThemeContext";
import { useSnack } from "../../../hooks/useSnack";
import { resetBreadcrumbs, setBreadcrumbDynamicPathNames } from "../../../redux/slices/breadcrumbsSlice";
import quizServices from "../../../services/quizServices";
import teacherServices from "../../../services/teacherServices";
import { selectStylesV2 } from "../../../styles/selectStyles";
import { getLanguage } from "../../../utils/getLanguage";

// Validation schemas
const newQuestionSchema = Yup.object({
  title: Yup.string().required("Question title is required"),
  options: Yup.array()
    .of(Yup.string().required("Option cannot be empty"))
    .min(2, "At least two options are required"),
  correctOption: Yup.number().nullable().required("Select a correct option"),
});

const editQuestionSchema = Yup.object({
  title: Yup.string().required("Question title is required"),
  options: Yup.array()
    .of(Yup.string().required("Option cannot be empty"))
    .min(2, "At least two options are required"),
  correctOption: Yup.number().nullable().required("Select a correct option"),
});

const TeacherViewAndEditQuizzes = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const lang = getLanguage();
  const { showSnack } = useSnack();
  const { quiz_id } = useParams();
  const dispatch = useDispatch();
  const suggestedAssessmentsModalRef = useRef();

  const isEditMode = !!quiz_id;

  const [teacherCourses, setTeacherCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assessmentName, setAssessmentName] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [quizType, setQuizType] = useState("");
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState({
    title: "",
    options: ["", ""],
    correctOption: null,
  });
  const [currentEditingQuestion, setCurrentEditingQuestion] = useState({
    title: "",
    options: ["", ""],
    correctOption: null,
  });
  const [editingIndex, setEditingIndex] = useState(null);

  const openSuggestedAssessmentsModal = () => suggestedAssessmentsModalRef.current?.openModal();

  const handleSuggestedAssessmentsSuccess = () => {
    // getTeacherClassrooms();
  };

  const getTeacherCourses = async () => {
    setLoading(true);
    try {
      const response = await teacherServices.getTeacherCourses();
      const formattedCourses = response?.map((course) => ({
        value: course?.id,
        label: course?.course,
      }));
      setTeacherCourses(formattedCourses);
    } catch (error) {
      console.error("Error fetching teacher courses:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEditMode) {
      (async () => {
        try {
          const response = await quizServices.getQuizzesByID(quiz_id);

          // Assuming the API returns quiz name, type, and questions
          setAssessmentName(response?.quiz?.name || "");
          setQuizType(response?.quiz?.type === 1 ? "quiz" : "homework");
          setSelectedCourse(response?.quiz?.course?.id);
          setQuestions(
            response?.questions?.map((q) => ({
              question_id: q.id,
              id: q.question.id, // Include the question id
              title: q.question.text, // Access the text from the nested 'question' object
              correctOption: q.question.options?.findIndex((opt) => opt.is_correct),
              options: q.question.options?.map((opt) => ({
                ...opt,
                label: opt.text, // Option text
                value: opt.id, // Option ID
              })),
            }))
          );

          dispatch(setBreadcrumbDynamicPathNames([response?.quiz?.name]));
        } catch (err) {
          console.error("Error fetching quiz data:", err);
        }
      })();
    }
  }, [quiz_id]);

  useEffect(() => {
    getTeacherCourses();
    return () => {
      dispatch(resetBreadcrumbs());
    };
  }, [lang]);

  const handleAddQuestion = async () => {
    const isValid = newQuestionSchema.isValidSync(currentQuestion);

    if (!isValid) {
      alert("Please fill in the new question correctly.");
      return;
    }

    try {
      const questionPayload = {
        question_type_id: 1,
        text: currentQuestion.title,
        image: null,
        options: currentQuestion.options.map((optText, index) => ({
          option_type_id: 1,
          text: optText,
          is_correct: currentQuestion.correctOption === index,
          image: null,
        })),
      };

      const payload = {
        course_id: selectedCourse,
        questions: [questionPayload],
      };

      const createResponse = await quizServices.createQuestion(payload);

      const quizPayload = {
        questions: createResponse.question_ids.map((q, index) => ({
          id: q,
          number_of_points: 0, // You can customize this or allow editing per question
        })),
      };
      if (createResponse?.question_ids?.length > 0) {
        const addResponse = await quizServices.createQuestionForAlreadyCreatedAssessment(
          quiz_id,
          quizPayload
        );
      }

      const createdQuestion = createResponse?.question_ids?.[0];

      if (!createdQuestion) {
        throw new Error("Failed to get created question ID");
      }

      const fetchResponse = await quizServices.getQuizzesByID(quiz_id);

      setQuestions(
        fetchResponse?.questions?.map((q) => ({
          question_id: q.id,
          id: q.question.id, // Include the question id
          title: q.question.text, // Access the text from the nested 'question' object
          correctOption: q.question.options?.findIndex((opt) => opt.is_correct),
          options: q.question.options?.map((opt) => ({
            ...opt,
            label: opt.text, // Option text
            value: opt.id, // Option ID
          })),
        }))
      );

      setCurrentQuestion({
        title: "",
        options: ["", ""],
        correctOption: null,
      });

      showSnack("Question added successfully", "success");
    } catch (error) {
      console.error(error);
      showSnack("Failed to add question", "error");
    }
  };

  const handleSaveChanges = async () => {
    const isValid = editQuestionSchema.isValidSync(currentEditingQuestion);

    // if (!isValid) {
    //   alert("Please fill in the edited question correctly.");
    //   return;
    // }

    try {
      const payload = {
        question_type_id: 1,
        course_id: selectedCourse,
        text: currentEditingQuestion.title,
        image: null,
      };
      await quizServices.editQuestion(currentEditingQuestion.id, payload);

      // Filter out only the options that don't have an `id` (i.e., new options)
      const newOptions = currentEditingQuestion.options
        .filter((opt) => !opt.id)
        .map((opt, index) => ({
          option_type_id: 1,
          text: opt.text,
          is_correct: currentEditingQuestion.correctOption === index,
          image: null,
        }));

      if (newOptions.length > 0) {
        const payloadTwo = { options: newOptions };
        await quizServices.addOption(currentEditingQuestion.id, payloadTwo);
      }

      const existingOptions = currentEditingQuestion.options.filter((opt) => opt.id);

      for (let index = 0; index < existingOptions.length; index++) {
        const opt = existingOptions[index];
        const payload = {
          option_type_id: 1,
          text: opt.text,
          is_correct: currentEditingQuestion.correctOption === index ? 1 : 0,
          image: null,
        };
        await quizServices.editOption(opt.id, payload); // 👈 assuming the API takes flat payload
      }

      // Get the latest question from the API
      const fetchResponse = await quizServices.getQuizzesByID(quiz_id);

      setQuestions(
        fetchResponse?.questions?.map((q) => ({
          question_id: q.id,
          id: q.question.id, // Include the question id
          title: q.question.text, // Access the text from the nested 'question' object
          correctOption: q.question.options?.findIndex((opt) => opt.is_correct),
          options: q.question.options?.map((opt) => ({
            ...opt,
            label: opt.text, // Option text
            value: opt.id, // Option ID
          })),
        }))
      );

      // Reset the editing state
      setEditingIndex(null);
      setCurrentEditingQuestion({ title: "", options: ["", ""], correctOption: null });
    } catch (error) {
      console.error("Error saving changes:", error);
    }
  };

  const handleDeleteQuestion = async (index) => {
    const questionToDelete = questions[index]; // Get the question to delete

    try {
      const response = await quizServices.deleteQuestionFromAlreadyCreatedAssessment(
        questionToDelete.question_id
      );

      // If successful, update the frontend state to reflect the deletion
      const updatedQuestions = questions.filter((_, qIndex) => qIndex !== index);
      setQuestions(updatedQuestions);
    } catch (error) {
      console.error("Error deleting the question:", error);
      // Optionally, you can handle the error (e.g., show a notification to the user)
    }
  };

  const handleEditQuestion = (index) => {
    const questionToEdit = questions[index];
    setCurrentEditingQuestion({
      id: questionToEdit.id,
      title: questionToEdit.title,
      options: questionToEdit.options,
      correctOption: questionToEdit.correctOption,
    });
    setEditingIndex(index);
  };

  const handleRemoveOption = async (index, isEditing = false) => {
    const targetQuestion = isEditing ? currentEditingQuestion : currentQuestion;
    const updatedOptions = targetQuestion.options.filter((_, oIndex) => oIndex !== index);
    if (isEditing) {
      if (currentEditingQuestion.options[index].id != undefined) {
        const response = await quizServices.deleteOption(currentEditingQuestion.options[index].id);
      }
      setCurrentEditingQuestion({ ...currentEditingQuestion, options: updatedOptions });
    } else {
      setCurrentQuestion({ ...currentQuestion, options: updatedOptions });
    }
  };

  const handleCreateQuiz = async (status) => {
    if (!selectedCourse) {
      showSnack(t("Course is required"), "error");
      return;
    }
    if (!quizType) {
      showSnack(t("Quiz type is required"), "error");
      return;
    }
    if (questions.length === 0) {
      showSnack(t("You must add at least one question"), "error");
      return;
    }

    const payload = {
      name: assessmentName,
      course_id: selectedCourse,
      type: quizType === "quiz" ? 1 : 2, // 1 for quiz, 2 for homework
      status, // 1 for draft, 2 for published
      questions: questions.map((q, index) => ({
        id: q.id,
        number_of_points: 0, // You can customize this or allow editing per question
      })),
    };

    try {
      const response = await quizServices.createQuizzes(payload);
      showSnack(response.message, "success");
      // Clear form after submission
      setSelectedCourse("");
      setQuizType("");
      setQuestions([]);
    } catch (error) {
      showSnack(error, "error");
      console.error(error);
    }
  };

  return (
    <div className="rounded-xl bg-white p-6 shadow-lg dark:bg-dark-surface">
      <div className="mb-5 flex items-center justify-between">
        <h3 className="text-2xl font-bold text-text dark:text-white">{t("assessment_details")}</h3>
        <div className="flex items-center justify-end space-x-4">
          <Button
            color="green"
            label={t("publish_assessment")}
            onClick={() => handleCreateQuiz(2)} // 2 = published
          />
          <Button
            color="white"
            label={t("save_as_draft")}
            onClick={() => handleCreateQuiz(1)} // 1 = draft
          />
        </div>
      </div>

      <form>
        <div className="mb-4">
          <p className="mb-2 text-sm font-medium text-mutedText dark:text-dark-mutedText">
            {t("assessment_name")}
          </p>
          <input
            type={"text"}
            name={"assessmentName"}
            placeholder={t("assessment_name")}
            className="block w-full rounded-md border bg-[#F9FAFB] px-3 py-2 text-sm placeholder:text-mutedText focus:border-blue-500 focus:ring focus:ring-blue-500 focus:ring-opacity-10 dark:border-dark-background dark:bg-dark-background dark:text-white"
            onChange={(e) => setAssessmentName(e.target.value)}
            value={assessmentName}
          />
          {assessmentName === "" && (
            <p className="text-xs text-red-300">{t("assessment_name_is_required")}</p>
          )}
        </div>
        <div className="mb-4">
          <p className="mb-2 text-sm font-medium text-mutedText dark:text-dark-mutedText">
            {t("select_course")}
          </p>
          <Select
            noOptionsMessage={() => <p>{t("no_options")}</p>}
            loadingMessage={() => <p>{t("loading")}</p>}
            className="z-[5]"
            isClearable={true}
            options={teacherCourses}
            value={teacherCourses.find((option) => option.value === selectedCourse)}
            onChange={(selectedOption) => setSelectedCourse(selectedOption?.value || "")}
            placeholder={t("select_course")}
            styles={selectStylesV2(theme)}
          />
          {selectedCourse === "" && <p className="text-xs text-red-300">{t("course_is_required")}</p>}
        </div>
        <p className="mb-2 text-sm font-medium text-mutedText dark:text-dark-mutedText">
          {t("select_quiz_or_homework")}
        </p>
        <div className="mb-4">
          <Select
            noOptionsMessage={() => <p>{t("no_options")}</p>}
            loadingMessage={() => <p>{t("loading")}</p>}
            className="z-[4]"
            isClearable={true}
            options={[
              { value: "quiz", label: t("quiz") },
              { value: "homework", label: t("homework") },
            ]}
            value={quizType ? { value: quizType, label: t(quizType) } : null}
            onChange={(selectedOption) => setQuizType(selectedOption?.value || "")}
            placeholder={t("select_quiz_or_homework")}
            styles={selectStylesV2(theme)}
          />
          {quizType === "" && <p className="text-xs text-red-300">{t("quiz_type_is_required")}</p>}
        </div>
        <div className="my-4 flex items-center justify-between">
          <p className="text-sm font-medium text-mutedText dark:text-dark-mutedText">
            {t("start_creating_questions")}
          </p>
          <Button label={t("suggested_assessments")} onClick={() => openSuggestedAssessmentsModal()} />
        </div>
        {questions.map((question, qIndex) => (
          <div
            key={qIndex}
            className="mb-4 rounded-lg border p-4 shadow-sm dark:border-gray-700 dark:bg-dark-background"
          >
            {editingIndex === qIndex ? (
              <div>
                <input
                  type="text"
                  value={currentEditingQuestion.title}
                  onChange={(e) =>
                    setCurrentEditingQuestion({ ...currentEditingQuestion, title: e.target.value })
                  }
                  className="mb-3 w-full rounded-md border p-2 dark:border-gray-700 dark:bg-dark-background dark:text-white"
                  placeholder={t("Question Title")}
                />
                <p className="mb-1 text-sm text-green-500">{t("select_correct_answer")} </p>

                {currentEditingQuestion.options.map((option, oIndex) => (
                  <div key={option.id} className="mb-2 flex items-center space-x-2">
                    <input
                      type="radio"
                      checked={currentEditingQuestion.correctOption === oIndex}
                      onChange={() =>
                        setCurrentEditingQuestion({ ...currentEditingQuestion, correctOption: oIndex })
                      }
                    />
                    <input
                      type="text"
                      value={option.text}
                      onChange={(e) => {
                        const updatedOptions = [...currentEditingQuestion.options];
                        updatedOptions[oIndex] = { ...updatedOptions[oIndex], text: e.target.value };
                        setCurrentEditingQuestion({ ...currentEditingQuestion, options: updatedOptions });
                      }}
                      className="w-full rounded-md border p-2 dark:border-gray-700 dark:bg-dark-background dark:text-white"
                      placeholder={t("Option")}
                    />
                    {oIndex > 1 && (
                      <Button
                        variant="outlined"
                        color="red"
                        label={t("remove")}
                        onClick={() => handleRemoveOption(oIndex, true)}
                      />
                    )}
                  </div>
                ))}

                <div className="flex items-center justify-between space-x-2">
                  <Button
                    variant="text"
                    color="white"
                    iconLeft={<MdAddCircleOutline size={20} />}
                    label={t("add_more_options")}
                    onClick={() =>
                      setCurrentEditingQuestion({
                        ...currentEditingQuestion,
                        options: [...currentEditingQuestion.options, ""],
                      })
                    }
                  />
                  <Button variant="outlined" label={t("save")} onClick={handleSaveChanges} />
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between">
                  <h4 className="text-lg font-semibold text-gray-800 dark:text-white">{question.title}</h4>
                  <div className="mt-2 flex justify-end space-x-2">
                    <Button
                      size="sm"
                      variant="outlined"
                      color="red"
                      label={t("delete")}
                      onClick={() => handleDeleteQuestion(qIndex)}
                    />
                    <Button
                      size="sm"
                      variant="outlined"
                      label={t("edit")}
                      onClick={() => handleEditQuestion(qIndex)}
                    />
                  </div>
                </div>
                <ul className="mt-2 space-y-2">
                  {question.options.map((option, oIndex) => (
                    <li key={oIndex} className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={oIndex === question?.correctOption}
                        className="accent-green-200"
                        readOnly
                      />
                      <span
                        className={
                          oIndex === question?.correctOption
                            ? "font-bold text-green-600 dark:text-green-400"
                            : "text-gray-700 dark:text-gray-300"
                        }
                      >
                        {option?.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
        {/* Add new question */}
        <div className="mb-4 rounded-lg border bg-blue-200 shadow-sm dark:border-gray-700 dark:bg-dark-background">
          <div className="px-5 pb-2 pt-5">
            {" "}
            <input
              type="text"
              value={currentQuestion.title}
              onChange={(e) => setCurrentQuestion({ ...currentQuestion, title: e.target.value })}
              className="mb-3 w-full rounded-md border p-2 dark:border-gray-700 dark:bg-dark-background dark:text-white"
              placeholder={t("question_title")}
            />
            <p className="mb-1 text-sm text-green-500">{t("select_correct_answer")}</p>
            {currentQuestion.options.map((option, oIndex) => (
              <div key={oIndex} className="mb-2 flex items-center space-x-2">
                <input
                  type="radio"
                  checked={currentQuestion.correctOption === oIndex}
                  onChange={() => setCurrentQuestion({ ...currentQuestion, correctOption: oIndex })}
                />
                <input
                  type="text"
                  value={option}
                  onChange={(e) => {
                    const updatedOptions = [...currentQuestion.options];
                    updatedOptions[oIndex] = e.target.value;
                    setCurrentQuestion({ ...currentQuestion, options: updatedOptions });
                  }}
                  className="w-full rounded-md border p-2 dark:border-gray-700 dark:bg-dark-background dark:text-white"
                  placeholder={t("option")}
                />
                {oIndex > 1 && (
                  <Button
                    variant="outlined"
                    color="red"
                    label={t("remove")}
                    onClick={() => handleRemoveOption(oIndex)}
                  />
                )}
              </div>
            ))}
            <div className="flex items-center justify-between">
              <Button
                variant="text"
                color="white"
                iconLeft={<MdAddCircleOutline size={20} />}
                label={t("add_more_options")}
                onClick={() =>
                  setCurrentQuestion({ ...currentQuestion, options: [...currentQuestion.options, ""] })
                }
              />
            </div>
          </div>
          <Button
            className="w-full rounded-tl-none rounded-tr-none"
            label={t("add_question")}
            onClick={handleAddQuestion}
          />
        </div>
      </form>
      <SuggestedAssessmentsModal
        ref={suggestedAssessmentsModalRef}
        onSubmit={handleSuggestedAssessmentsSuccess}
      />
    </div>
  );
};

export default TeacherViewAndEditQuizzes;
